import { Environment, Metadata, Plan } from '@workspaces/types'
import BrowserEnvironmentResolver from './environment.helper'
import PlanService from '@/services/plan.service'
import { usePlanStore } from '@/store/plan'
import {
  getRealtimeReactionForPlans,
  isPlansRealtimeFeatureEnabled,
} from '@/plan/metadata/metadata.helper'
import { subscribeToPlans as subscribeToPlansService } from '@workspaces/services'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { getInstance as getAuth0ServiceInstance } from '@/auth/index'

async function fetchMyPlans(
  userId: string,
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  try {
    const plans = await PlanService.getPlansByUserId(meta, environment, userId)
    const plansFormatted: Plan.Plan[] = plans.map((p: Plan.Plan) => {
      return {
        ...p,
        modified: p.modified_at?.toISOString(),
        user: {
          id: p.created_by,
          name: p.created_by.split('@')[0],
        },
      }
    })
    usePlanStore().setMyPlans(plansFormatted)
    console.debug('📒 My plans: ', plansFormatted.length)
  } catch (error) {
    usePlanStore().setMyPlans([])
  }
}

async function fetchPublicPlans(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  try {
    const plans = await PlanService.getPublicPlans(meta, environment)
    const plansFormatted: Plan.Plan[] = plans.map((p: Plan.Plan) => {
      return {
        ...p,
        modified: p.modified_at?.toISOString(),
        user: {
          id: p.created_by,
          name: p.created_by.split('@')[0],
        },
      }
    })
    usePlanStore().setPublicPlans(plansFormatted)
    console.debug('📒 Public plans: ', plansFormatted.length)
  } catch (error) {
    usePlanStore().setPublicPlans([])
  }
}

async function fetchPlans(
  userId: string,
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  await Promise.all([
    fetchMyPlans(userId, meta, environment),
    fetchPublicPlans(meta, environment),
  ])
}

export async function fetchPlansVisibleByUser(): Promise<void> {
  console.debug('📒 Fetching plans visible by user')
  // getUser() returns the authenticated user id; here it is always a string
  const userId = getAuth0ServiceInstance().getUser() as string
  const meta = getAppMetadata()
  const environment = BrowserEnvironmentResolver.getInstance()
  await fetchPlans(userId, meta, environment)
}

export async function subscribeToPlans(
  meta: Metadata.AppMetadata,
  environment: BrowserEnvironmentResolver,
): Promise<void> {
  if (!isPlansRealtimeFeatureEnabled(meta)) {
    return
  }
  const planStore = usePlanStore()
  const isSubscribed = planStore.getIsSubscribedToPlans
  if (isSubscribed) {
    console.warn(
      'Already subscribed to plans. This should be not happening, please check how this is being called',
    )
    console.debug('📒 ✋ Already subscribed to plans. Nothing to do')
    return
  }
  console.debug('📒📒📒📒 Subscribing to plans 📒📒📒📒')
  const debounceDelay = getRealtimeReactionForPlans(meta)
  console.debug('📒 Debounce delay for plans in seconds: ', debounceDelay)
  const unscribeFunction = await subscribeToPlansService(
    meta,
    environment,
    debounceDelay,
    fetchPlansVisibleByUser,
  )
  window.addEventListener('beforeunload', () => {
    if (unscribeFunction) {
      console.debug('📒 ✅ Unsubscribing from plans before closing')
      unscribeFunction()
      planStore.setIsSubscribedToPlans(false)
    }
  })
  planStore.setIsSubscribedToPlans(true)
}
